import { Component, OnDestroy } from '@angular/core';
import { GridOptions } from 'ag-grid';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';
import { IApiResponse } from 'app/core/interfaces/invoice-external.interface';
import { IServiceProvider } from 'app/core/interfaces/service-provider.interface';
import { ServiceProviderService } from 'app/core/services/service-provider.service';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { Subscription } from 'rxjs/Subscription';
import { EProviderSelectionLimits } from 'app/core/enums/provider-tabs.enum';

@Component({
  selector: 'app-merge-service-provider',
  templateUrl: './merge-service-provider.component.html',
  styleUrls: ['./merge-service-provider.component.scss'],
})
export class MergeServiceProviderComponent implements OnDestroy {
  protected gridServiceProviders: GridOptions;
  selectServiceProviderId: number;
  selectDescription: string;
  public gridApi: any;
  private subscriptions: Subscription[] = [];
  private servicesProvidersSelected: IServiceProvider[] = [];
  private minMergeServiceProviders = 2;

  constructor(
    private serviceProviderService: ServiceProviderService,
    private sweetAlertHelper: SweetAlertHelper,
    protected _loading: T2LoadingService
  ) {
    this.getServiceProviderIds();
    this.loadGrids();
  }

  get disableButtonMerge(): boolean {
    return !this.selectServiceProviderId || this.servicesProvidersSelected.length < this.minMergeServiceProviders;
  }

  private getServiceProviderIds(): void {
    this.subscriptions.push(
      this.serviceProviderService.serviceProviders$.subscribe((value: IServiceProvider[]) => {
        if (value && value.length && this.gridServiceProviders) {
          this.servicesProvidersSelected = value;
          this.gridServiceProviders.api.setRowData(value);
        }
      }),
      this.serviceProviderService.resetMergeSelection$.subscribe((reset) => {
        if (reset) {
          this.gridApi.deselectAll();
           this.serviceProviderService.notifyModalClosed();
        }
      })
    );
  }

  private loadGrids(): void {
    this.gridServiceProviders = <GridOptions>{
      enableColResize: true,
      rowSelection: 'single',
      suppressRowClickSelection: true,
      columnDefs: this.columnsDefinitions(),
      enableSorting: true,
      enableFilter: true,
      autoresizeColumns: true,
      onGridReady: (params) => {
        params.api.setRowData([]);
        window.addEventListener('resize', () => {
          params.api.sizeColumnsToFit();
        });
        this.gridApi = params.api;
        params.api.sizeColumnsToFit();
      },
      onRowSelected: (event) => {
        if (!event.api) {
          return;
        }

        const selectedRows = event.api.getSelectedRows();
        if (!selectedRows.length) {
          this.selectDescription = '';
          this.selectServiceProviderId = null;
          return;
        }
        selectedRows.map((selectedRow) => {
          const { description, serviceproviderid } = selectedRow;
          this.selectDescription = description || '';
          this.selectServiceProviderId = serviceproviderid;
        });
      },
    };
  }

  private columnsDefinitions() {
    return [
      {
        checkboxSelection: true,
        headerCheckboxSelection: false,
        width: 30,
      },
      {
        headerName: 'Description',
        field: 'description',
        width: 250,
      },
      {
        headerName: 'Speed Code',
        field: 'speedcode',
        width: 87,
      },
      {
        headerName: 'Street Address',
        field: 'streetaddress',
        width: 170,
      },
      {
        headerName: 'Apt/Room',
        field: 'roomaptnumber',
        width: 100,
      },
      {
        headerName: 'City',
        field: 'city',
        width: 143,
      },
      {
        headerName: 'State',
        field: 'state',
        width: 60,
      },
      {
        headerName: 'Zip',
        field: 'zip',
        width: 91,
      },
      {
        headerName: 'County',
        field: 'county',
        width: 156,
      },
      {
        headerName: 'Remove',
        field: 'remove',
        cellClass: 'fa fa-trash',
        cellStyle: { color: 'red', 'font-size': '15px', 'cursor': 'pointer', 'text-align': 'center' },
        width: 80,
        onCellClicked: (params) => {
          if (params && params.data && params.data.serviceproviderid) {
            this.removeServiceProvider(params.data.serviceproviderid);
          }
        }
      },
    ];
  }

  private getAllRowsFromGrid(): any[] {
    if (!this.gridApi) {
      return [];
    }
    const allNodes = this.gridApi.getModel().rowsToDisplay;
    return allNodes
      .map((node) => node.data.serviceproviderid)
      .filter((id) => id !== this.selectServiceProviderId);
  }

  send(): void {
    if (!this.selectDescription) {
      return;
    }

    const payload = {
      serviproviderkeep: this.selectServiceProviderId,
      serviprovidermerge: this.getAllRowsFromGrid(),
    };

    this._loading.show();

    this.subscriptions.push(
      this.serviceProviderService.putServiceProvidersMerge(payload)
        .finally(() => this._loading.hiden())
        .subscribe((response: IApiResponse) => {
          this.sweetAlertHelper.createCustomAlert({
            title: response.message,
            type: response.success ? 'success' : 'error',
          });
          this.serviceProviderService.saveOnSuccessfull$.emit(true);
          this.closeModal(true, true);
        }, (err) => {
          this.sweetAlertHelper.captureException(err);
        })
    );
  }

  closeModal(refreshGrid = false, cleanServiceProviders = false): void {
    this.serviceProviderService.setCloseMergeServiProviders(true, refreshGrid, cleanServiceProviders);
    this.gridApi.deselectAll();
    this.serviceProviderService.notifyModalClosed();

  }

  private removeServiceProvider(id: number): void {
    if (this.servicesProvidersSelected.length <= EProviderSelectionLimits.MIN_SELECTED) {
      return;
    }
    const index = this.servicesProvidersSelected.findIndex(sp => sp.serviceproviderid === id);
    if (index < 0) {
      return;
    }

    if (this.selectServiceProviderId === id) {
      this.clearSelectedServiceProvider();
    }

    this.servicesProvidersSelected.splice(index, 1);
    this.serviceProviderService.setServiceProviders(this.servicesProvidersSelected);
  }

  private clearSelectedServiceProvider(): void {
    this.selectServiceProviderId = null;
    this.selectDescription = '';
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/service-provider/components/merge-service-provider/merge-service-provider.component.ts